import React, { Component, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {
  Paper,
  Button,
  TextField,
  Divider,
  Typography,
  SvgIcon,
  MenuItem
} from '@material-ui/core';

import { ChevronRight } from '@material-ui/icons';
import { Link } from 'react-router-dom';
import { calculateShipping, fetchStates } from '../../redux/actions/shipping';
import numberWithCommas from '../../utils/numberWithCommas';
import {
  SHIPPING_ESTIMATE_REQUESTED,
  SHIPPING_ESTIMATE_RETRIEVED,
  SHIPPING_ESTIMATE_FAILED
} from '../../redux/actions/types';
import { ReactComponent as TruckIcon } from '../../images/Icons/truck.svg';
import { ReactComponent as MapPinIcon } from '../../images/Icons/map-pin.svg';
import { ReactComponent as CreditCardIcon } from '../../images/Icons/creditcard-01.svg';
import { ReactComponent as BankTransferIcon } from '../../images/Icons/banktransfers-01.svg';
import { ReactComponent as RefreshIcon } from '../../images/Icons/refresh-cw.svg';
import { ReactComponent as ShieldIcon } from '../../images/Icons/shield.svg';
import { ReactComponent as SellerIcon } from '../../images/Icons/vendedora.svg';
import MercadoPagoIcon from '../../images/MERCADOPAGO.jpg';
import AdornedButton from '../../commons/AdornedButton';
import RTVSelect from '../../commons/inputs/RTVSelect';
import RegionPicker from '../../commons/inputs/RegionPicker';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import RTVErrorDialog from '../../commons/dialogs/RTVErrorDialog';

const styles = theme => ({
  root: {
    margin: theme.spacing(2),
    width: '80%',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  paper: {
    padding: theme.spacing(2),
    width: '100%',
    borderRadius: 0,
    '& > *': {
      margin: theme.spacing(2)
    }
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 800,
    marginBottom: theme.spacing(1),
    '& > *': {
      fontWeight: 800
    }
  },
  headerLeft: {
    flex: 1
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    '&:hover': {
      cursor: 'pointer',
      textDecoration: 'underline'
    }
  },
  payment: {
    margin: theme.spacing(1, 1, 2, 1)
  },
  paymentHeader: {
    display: 'flex',
    alignItems: 'flex-start'
  },
  icon: {
    marginRight: theme.spacing(1)
  },
  shippingForm: {
    display: 'flex',
    flexFlow: 'column'
  },
  childMargin: {
    '& > *': {
      margin: theme.spacing(1)
    }
  },
  inlineForm: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-evenly',
    '& > *': {
      margin: theme.spacing(1)
    }
  },
  costButton: {
    flex: 1,
    marginTop: theme.spacing(1),
    padding: 0,
    whiteSpace: 'nowrap',
    fontWeight: 500,
    '& > *': {
      padding: 0
    }
  },
  costSubmitSection: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center'
  },
  shippingInput: {
    fontSize: '1rem'
  },
  optionTitle: {
    fontWeight: 500
  },
  optionSubtitle: {
    color: '#707070',
    fontSize: '0.9rem',
    paddingLeft: '2rem'
  },
  postalCodeInput: {
    width: '100px'
  },
  cost: {
    flex: 1,
    fontWeight: 800
  },
  statesForm: {
    marginTop: theme.spacing(2)
  },
  statesInput: {
    width: '100%',
    padding: theme.spacing(1),
    paddingTop: 0,
    paddingLeft: 0
  },
  regionInput: {
    padding: theme.spacing(1),
    paddingRight: theme.spacing(3),
    paddingLeft: 0
  }
});

const ChileRegionSelector = ({ classes, setPostalCode }) => {
  const [state, setState] = React.useState('');
  const [region, setRegion] = React.useState('');

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const states = useSelector(state => state.shipping.states);

  useEffect(() => {
    dispatch(fetchStates());
  }, [dispatch]);

  const handleShippingChanges = (field, value) => {
    if (field === 'selectedState') {
      if (value === state?.id) {
        return;
      }
      setState(value);
      setRegion('');
      setPostalCode('');
    }

    if (field === 'selectedRegion') {
      if (value === region?.id) {
        return;
      }
      setRegion(value);
      setPostalCode(value.postal_code);
    }
  };

  return (
    <div className={classes.statesForm}>
      <RTVSelect
        value={state}
        label={i18next.format(t('provincia'), 'capitalize')}
        onChange={({ target: { value } }) =>
          handleShippingChanges('selectedState', value)
        }
        className={classes.statesInput}
      >
        {states.map(currentState => (
          <MenuItem key={currentState.id} value={currentState}>
            {currentState.name}
          </MenuItem>
        ))}
      </RTVSelect>
      <RegionPicker
        id="region-picker-shipping"
        label={i18next.format(t('localidad'), 'capitalize')}
        value={region && region.id}
        onChange={(_, newValue) => {
          handleShippingChanges('selectedRegion', newValue);
        }}
        state={state?.id}
        className={classes.regionInput}
      />
    </div>
  );
};

class Shipping extends Component {
  constructor(props) {
    super(props);
    this.state = {
      postalCode: '',
      calculatingShipping: false,
      errorShipping: false,
      shippingEstimate: null,
      shippingErrorStatus: ''
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.shippingEstimate !== this.props.shippingEstimate) {
      this.setState({ shippingEstimate: this.props.shippingEstimate });
    }

    if (prevProps.shippingStatus !== this.props.shippingStatus) {
      if (this.props.shippingStatus === SHIPPING_ESTIMATE_REQUESTED) {
        this.setState({
          calculatingShipping: true,
          errorShipping: false
        });
      }
      if (this.props.shippingStatus === SHIPPING_ESTIMATE_RETRIEVED) {
        this.setState({
          calculatingShipping: false
        });
      }
      if (this.props.shippingStatus === SHIPPING_ESTIMATE_FAILED) {
        this.setState({
          calculatingShipping: false,
          shippingErrorStatus: this.props.shippingErrorStatus,
          errorShipping: true
        });
      }
    }
  }

  handleChange = (type, value) => {
    this.setState({ [type]: value });
  };

  calculateCost = () => {
    this.props.calculateShipping(this.state.postalCode, [this.props.product]);
  };

  resetCost = () => this.setState({ shippingEstimate: null });

  render() {
    const { classes, user } = this.props;

    return (
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <div className={classes.shippingForm}>
            <div className={classes.header}>
              <SvgIcon className={classes.icon}>
                <TruckIcon />
              </SvgIcon>
              <Typography>Envíos a todo el país</Typography>
            </div>
            <ChileRegionSelector
              classes={classes}
              setPostalCode={value => this.handleChange('postalCode', value)}
            />
            <div className={classes.inlineForm}>
              {this.state.shippingEstimate ? (
                <>
                  <Typography className={classes.cost}>
                    ${numberWithCommas(this.state.shippingEstimate.value)} a la
                    sucursal de correo más cercana
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.costButton}
                    onClick={this.resetCost}
                  >
                    Volver a estimar
                  </Button>
                </>
              ) : (
                <>
                  {process.env.REACT_APP_COUNTRY === 'AR' && (
                    <TextField
                      className={classes.postalCodeInput}
                      error={this.state.errorShipping}
                      InputProps={{
                        classes: {
                          input: classes.shippingInput
                        }
                      }}
                      disabled={this.state.calculatingShipping}
                      helperText="Código postal"
                      value={this.state.postalCode}
                      onChange={({ target: { value } }) =>
                        this.handleChange('postalCode', value)
                      }
                    />
                  )}
                  {process.env.REACT_APP_COUNTRY === 'CL' && (
                    <RTVErrorDialog
                      open={this.state.errorShipping}
                      onClose={() => this.setState({ errorShipping: false })}
                      error={this.state.shippingErrorStatus}
                      action="estimateShipping"
                    />
                  )}
                  <AdornedButton
                    variant="contained"
                    color="primary"
                    className={classes.costButton}
                    disabled={
                      this.state.postalCode.length < 4 ||
                      this.state.calculatingShipping
                    }
                    loading={this.state.calculatingShipping}
                    onClick={this.calculateCost}
                  >
                    Estimar costo de envío
                  </AdornedButton>
                </>
              )}
            </div>
          </div>
          <Divider />
          {(user.type === 'ROLE_CELEBRITY' || user.type === 'ROLE_VIP') &&
            process.env.REACT_APP_COUNTRY === 'AR' && (
              <>
                <div>
                  <div className={classes.header}>
                    <SvgIcon className={classes.icon}>
                      <MapPinIcon />
                    </SvgIcon>
                    <Typography>Opción de retiro sin cargo</Typography>
                  </div>
                  <Typography style={{ paddingLeft: '2rem', color: '#707070' }}>
                    CABA
                  </Typography>
                </div>
                <Divider />
              </>
            )}
          <div className={classes.paymetOptions}>
            <div className={classes.header}>
              <Typography className={classes.headerLeft}>
                {i18next.t('payWith', { ns: 'products' })}
              </Typography>
              <Typography
                className={classes.headerRight}
                fontSize="small"
                component="a"
                href={
                  process.env.REACT_APP_COUNTRY === 'AR'
                    ? "https://renovatuvestidor.zendesk.com/hc/es/articles/360056405332-C%C3%B3mo-puedo-pagar-mi-compra-"
                    : 'https://renuevatuclosetcl.zendesk.com/hc/es/articles/205148717--Con-qu%C3%A9-medios-de-pago-puedo-hacer-mi-compra-'
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                ver todos
                <ChevronRight fontSize="small" color="primary" />
              </Typography>
            </div>
            <div className={classes.payment}>
              <div className={classes.paymentHeader}>
                <SvgIcon className={classes.icon}>
                  <CreditCardIcon />
                </SvgIcon>
                <Typography className={classes.optionTitle}>
                  Tarjetas de crédito y débito
                </Typography>
              </div>
              <Typography className={classes.optionSubtitle}>
                Hasta 3 cuotas sin interés!
              </Typography>
            </div>
            <div className={classes.payment}>
              <div className={classes.paymentHeader}>
                <SvgIcon className={classes.icon}>
                  <BankTransferIcon />
                </SvgIcon>
                <Typography className={classes.optionTitle}>
                  Efectivo y transferencia bancaria
                </Typography>
              </div>
            </div>
            <div className={classes.payment}>
              <div className={classes.paymentHeader}>
                <img
                  className={classes.icon}
                  width="24"
                  height="24"
                  src={MercadoPagoIcon}
                  alt="mercado pago"
                />
                <Typography className={classes.optionTitle}>
                  Mercado Pago
                </Typography>
              </div>
            </div>
          </div>
          <Divider />
          <div className={classes.securePurchase}>
            <div className={classes.header}>
              <Typography className={classes.headerLeft}>
                {i18next.t('paySafe', { ns: 'products' })}
              </Typography>
              <Typography
                component={Link}
                to="/como-funciona/como-comprar"
                target="_blank"
                className={classes.headerRight}
                fontSize="small"
              >
                más info
                <ChevronRight fontSize="small" color="primary" />
              </Typography>
            </div>
            <div className={classes.payment}>
              <div className={classes.paymentHeader}>
                <SvgIcon className={classes.icon}>
                  <RefreshIcon />
                </SvgIcon>
                <Typography className={classes.optionTitle}>
                  Tus compras tienen cambio
                </Typography>
              </div>
            </div>
            <div className={classes.payment}>
              <div className={classes.paymentHeader}>
                <SvgIcon className={classes.icon}>
                  <ShieldIcon />
                </SvgIcon>
                <Typography className={classes.optionTitle}>
                  Compra protegida
                </Typography>
              </div>
              <Typography className={classes.optionSubtitle}>
                {i18next.t('receiveYourProduct', { ns: 'products' })}
              </Typography>
            </div>
            <div className={classes.payment}>
              <div className={classes.paymentHeader}>
                <SvgIcon className={classes.icon}>
                  <SellerIcon />
                </SvgIcon>
                <Typography className={classes.optionTitle}>
                  Sin necesidad de encontrarte con tu vendedora
                </Typography>
              </div>
            </div>
          </div>
        </Paper>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  shippingStatus: state.shipping.status,
  shippingEstimate: state.shipping.estimate,
  shippingErrorStatus: state.shipping.error
});

const mapDispatchToProps = dispatch => ({
  calculateShipping: (postalCode, products) =>
    dispatch(calculateShipping(postalCode, products))
});

export default withStyles(styles)(
  connect(mapStateToProps, mapDispatchToProps)(Shipping)
);
